import React, { useState } from 'react'
import { Form, Button, Row, Col } from 'react-bootstrap'
import { useForm, Controller } from "react-hook-form";
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next'
import vlMessages from 'Constants/ValidationMessages'
import ErrorMessage from 'Components/ErrorMessage'
import * as yup from 'yup';
import CurrencyInput from 'react-currency-input-field';
import numeral from 'numeral'
import Utilities from 'Utilities'
/**
 * 16/08/2021: el import de numeral/locales/es es muy importante, no borrar.
 *              si es posible buscar una alternativa a Numeral Js
 */
import 'numeral/locales/es'
import useStores from 'hooks/useStores';
import { bindRef } from 'Utilities/formRef'
numeral.locale('es')

/** API envía Percentage; legacy usaba Value. */
function taxPercentageOf(tax) {
    if (!tax) return 0
    const raw = tax.Percentage ?? tax.Value ?? 0
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
}

function parseAmount(value) {
    if (value === undefined || value === null || value === '') return 0
    const n = numeral(value).value()
    return n == null || Number.isNaN(n) ? 0 : n
}

const FormSection = ({ backToListHandler }) => {

    const { t } = useTranslation();
    
    const {invoicesStore, configurationStore, uiStore} = useStores()

    const validationSchema = yup.object().shape({
        TaxBase: yup.string(),
    })

    const { register, handleSubmit, errors, reset, setValue, getValues, control } = useForm({ validationSchema: validationSchema });

    // Si el usuario retrocede, se mantienen los montos y al volver a este forumario
    // se debe determinar cual fue el id más alto asignado anteriormente
    const [idCounter, setIdCounter] = useState(
        invoicesStore.amounts.length ? Math.max.apply(Math, invoicesStore.amounts.map(function (amount) { return amount.Row + 1; })) : 1)

    // Controla que no se muestre el resto del formulario
    // hasta que se seleccione un impuesto valido
    const [isValidSelectedTax, setIsValidSelectedTax] = useState(false)
    const [taxPercentage, setTaxPercentage] = useState(0)

    // Para el calculo automatico
    const [totalTax, setTotalTax] = useState('0.00')
    const [totalAmount, setTotalAmount] = useState('0.00')

    function onAmountSubmit(data) {
        const payload = {
            TaxType: parseInt(data.TaxType, 10),
            TaxPorcentage: taxPercentage,
            TaxBase: parseAmount(data.TaxBase),
            IdInvoice: '',
            Row: idCounter,
            TaxAmmount: parseAmount(totalTax),
            TotalAmmount: parseAmount(totalAmount),
            TaxFree: taxPercentage === 0 ? parseAmount(totalAmount) : 0,
            Discount: 0,
        }

        invoicesStore.addAmount(payload)
        uiStore.addNotification(uiStore.appNotifications.SUCCESS_ADDED)
        setIdCounter(idCounter + 1)
        setIsValidSelectedTax(false)
        setTaxPercentage(0)
        setTotalTax('0.00')
        setTotalAmount('0.00')
        reset()
        backToListHandler()
    }

    function handleTaxTypeChange(e) {
        const taxTypeId = parseInt(e.target.value, 10)
        const tax = configurationStore.taxes.find(x => Number(x.Id) === taxTypeId)
        const percentage = taxPercentageOf(tax)
        setIsValidSelectedTax(true)
        setTaxPercentage(percentage)
        setValue('TaxPorcentage', percentage)
        calculateTaxandTotal(getValues('TaxBase'), percentage)
    }

    // el valor va entre [] porque react-hook-form Controller empaqueta los args de onChange
    function handleTaxBaseChange([value]) {
        calculateTaxandTotal(value, taxPercentage)
        return value
    }

    function calculateTaxandTotal(base, percentage = taxPercentage) {
        const baseNum = parseAmount(base)
        const taxNum = baseNum * (percentage / 100)
        const totalNum = baseNum + taxNum
        setTotalTax(only2Decimals(Utilities.FormatCurrency(taxNum)))
        setTotalAmount(only2Decimals(Utilities.FormatCurrency(totalNum)))
    }

    function handleTotalTaxChange(value) {
        setTotalTax(value)
        const totalNum = parseAmount(getValues('TaxBase')) + parseAmount(value)
        setTotalAmount(only2Decimals(Utilities.FormatCurrency(totalNum)))
    }

    function handleTotalAmountChange(value) {
        setTotalAmount(value)
        const totalNum = parseAmount(value)
        if (taxPercentage === 0) {
            setValue('TaxBase', only2Decimals(Utilities.FormatCurrency(totalNum)))
            setTotalTax('0.00')
            return
        }
        const baseNum = totalNum / (1 + taxPercentage / 100)
        const taxNum = totalNum - baseNum
        setValue('TaxBase', only2Decimals(Utilities.FormatCurrency(baseNum)))
        setTotalTax(only2Decimals(Utilities.FormatCurrency(taxNum)))
    }

    function only2Decimals(value){
        return numeral(value).format('0.00')
    }

    return (
        <Form onSubmit={handleSubmit(onAmountSubmit)} className="">
            <b className="text-center"><span>{t('amount_information')}</span></b>
            <Form.Group controlId="TaxTypegr">
                <Form.Label>{t('vat_type')}:</Form.Label>
                
                <Form.Control as="select"
                    name="TaxType"
                    defaultValue=""
                    onChange={handleTaxTypeChange}
                    ref={bindRef(register, { required: vlMessages.REQUIRED })}
                >
                    <option value="" disabled>{t('select_for_continue')}</option>
                    {configurationStore.taxes.map(tax => {
                        const pct = taxPercentageOf(tax)
                        return (
                            <option key={tax.Id} value={tax.Id}>
                                {tax.Description} ({pct}%)
                            </option>
                        )
                    })}
                </Form.Control>
                <ErrorMessage error={errors?.TaxType} />
            </Form.Group>
            <Form.Control type="hidden" name="TaxPorcentage" ref={bindRef(register)} />
            {isValidSelectedTax && <>
                <Form.Group controlId="blBase">
                    <Form.Label>{t('tax_base')}</Form.Label>
                    <Controller
                        as={CurrencyInput}
                        allowDecimals={true}
                        decimalsLimit={2}
                        className="form-control"
                        decimalSeparator=","
                        groupSeparator="."
                        onChange={handleTaxBaseChange}
                        name="TaxBase"
                        control={control}
                    />
                    <ErrorMessage error={errors?.TaxBase} />
                </Form.Group>

                <Form.Group controlId="total_tax">
                    <Form.Label>{t('vat')}
                    </Form.Label>
                    <CurrencyInput
                        id="taxTotal"
                        allowDecimals={true}
                        decimalsLimit={2}
                        className="form-control"
                        value={totalTax}
                        onChange={handleTotalTaxChange}
                        decimalSeparator=","
                        groupSeparator="."
                />
                </Form.Group>
                <Form.Group controlId="total">
                    <Form.Label>{t('total')}</Form.Label>
                    <CurrencyInput
                        id="totalAmount"
                        allowDecimals={true}
                        decimalsLimit={2}
                        className="form-control"
                        value={totalAmount}
                        onChange={handleTotalAmountChange}
                        decimalSeparator=","
                        groupSeparator="."
                />
                </Form.Group>
            </>}

            <Row>
                <Col>
                    <Button
                        className="w-100"
                        variant="secondary"
                        onClick={backToListHandler}>
                        {t('back')}
                    </Button>
                </Col>
                <Col>
                    <Button
                        disabled={!isValidSelectedTax}
                        className="w-100"
                        type="submit">
                        {t('add')}
                    </Button>
                </Col>
            </Row>
        </Form>
    )
}

export default observer(FormSection)
